import React, { useState } from "react";
import { Star, Quote } from "lucide-react";

const ReviewCard = ({ review }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Custom date formatting function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${month} ${day}, ${year}`;
  };

  const formattedDate = review.created_at ? formatDate(review.created_at) : "";
  const reviewerName = review.name || "Anonymous";
  const rating = review.rating || 0;
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  // Get initials for avatar
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <div 
      className={`bg-white rounded-lg overflow-hidden shadow-md transition-all duration-300 border ${
        isHovered 
          ? "shadow-lg border-[#D4AF37]/30 translate-y-px" 
          : "shadow-sm border-gray-100"
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Premium accent line with gradient animation */}
      <div 
        className={`h-0.5 bg-gradient-to-r from-[#D4AF37] via-[#C19B2E] to-[#D4AF37] transition-all duration-500 ${
          isHovered ? "opacity-100" : "opacity-70"
        }`}
        style={{
          backgroundSize: isHovered ? "200% 100%" : "100% 100%",
          backgroundPosition: isHovered ? "right center" : "left center"
        }}
      />

      <div className="p-4 sm:p-5">
        {/* Product Image Section */}
        {review.image && (
          <div className="mb-3">
            <div className={`relative rounded-md overflow-hidden transition-all duration-300 ${
              isHovered ? "ring-2 ring-[#D4AF37]" : "ring-1 ring-[#D4AF37]/50"
            }`}>
              <img
                src={review.image}
                alt={review.product_name || "Product"}
                className="w-full h-36 sm:h-40 object-cover bg-amber-50 aspect-square"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
              {review.product_name && (
                <div className={`absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/70 to-transparent transition-opacity duration-300 ${
                  isHovered ? "opacity-100" : "opacity-80"
                }`}>
                  <p className="text-white text-xs font-medium truncate">
                    {review.product_name}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
          <div className="flex items-center space-x-3">
            {/* Reviewer Avatar with initials */}
            <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              isHovered 
                ? "bg-gradient-to-br from-[#D4AF37]/20 to-[#C19B2E]/20 border-2 border-[#D4AF37]" 
                : "bg-gradient-to-br from-amber-50 to-amber-200 border-2 border-amber-200"
            }`}>
              <span className="text-sm font-medium text-[#C19B2E]">
                {getInitials(reviewerName)}
              </span>
            </div>
            
            <div>
              <h3 className="text-sm sm:text-base font-medium text-[#333333]">
                {reviewerName}
              </h3>
              {formattedDate && (
                <p className="text-xs text-gray-500 mt-0.5 font-light">{formattedDate}</p>
              )}
            </div>
          </div>
          
          {/* Rating Badge */}
          <div className={`flex items-center px-2 py-1 rounded-full transition-all duration-300 ${
            isHovered 
              ? "bg-gradient-to-r from-[#D4AF37]/10 to-amber-50 border border-[#D4AF37]/50" 
              : "bg-amber-50 border border-amber-100"
          }`}>
            <span className="text-[#C19B2E] font-semibold text-sm mr-1">
              {rating.toFixed(1)}
            </span>
            <div className="flex space-x-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  className={`transition-colors duration-300 ${
                    i < fullStars
                      ? "text-[#D4AF37] fill-[#D4AF37]"
                      : i === fullStars && hasHalfStar
                      ? "text-[#D4AF37]"
                      : "text-amber-200"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Review Content */}
        {review.comment && (
          <div className="mt-3 relative">
            <Quote 
              size={16} 
              className={`absolute -left-1 -top-1 text-[#D4AF37]/50 transition-opacity duration-300 ${
                isHovered ? "opacity-100" : "opacity-60"
              }`} 
            />
            <p className="text-gray-700 text-xs sm:text-sm leading-relaxed font-light pl-4 italic">
              {review.comment}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewCard;